import { Modal, TextField } from 'ui'
import { ModalCloseButton } from 'widget/ModalCloseButton/ModalCloseButton'
import style from './LkmAppealOfferModal.module.scss'
import classNames from 'classnames'
import { Controller, FieldValues, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { orderFormSchema } from '../../config/OrderFormSchema'
import { orderFormDefaultValues } from '../../config/OrderFormDefaultValues'
import { Severity } from 'widget/Notifications/types'
import { useSetNotice } from 'hooks/useSetNotice'
import { OrderFormRequest, TErrorResponse } from 'feature/lkmAppeals/types'
import { useState } from 'react'
import { LkmAppealOfferDateField } from './components/LkmAppealOfferDateField'
import { LkmAppealOfferButtons } from './components/LkmAppealOfferButtons'

type LkmAppealOfferModalProps = {
  open: boolean
  onClose: () => void
  description: {
    id: number
    name?: string
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  submitOrderForm: (data: OrderFormRequest) => Promise<any>
  isLoading: boolean
}

export function LkmAppealOfferModal({
  open,
  onClose,
  description,
  submitOrderForm,
  isLoading,
}: LkmAppealOfferModalProps) {
  const [isValidDate, setValidDate] = useState(true)

  const setNotice = useSetNotice()
  const formMethods = useForm({
    defaultValues: orderFormDefaultValues,
    resolver: yupResolver(orderFormSchema),
    mode: 'all',
  })
  const {
    formState: { errors, isValid },
    handleSubmit,
    control,
    reset,
  } = formMethods

  const handleClose = () => {
    reset()
    onClose()
  }

  const isSubmitButtonDisabled =
    isLoading || !isValid || !!Object.entries(errors).length || !isValidDate

  const handleSubmitOrderForm = async (data: FieldValues) => {
    try {
      const res = await submitOrderForm({
        claimId: description.id,
        amount: Number(data.price),
        comment: data.comment,
        installationDate: data.date,
      })
      if ('error' in res) {
        throw new Error((res as TErrorResponse).error.data.message)
      } else {
        handleClose()
        setNotice('Отклик успешно отправлен', Severity.success)
      }
    } catch (error: unknown) {
      setNotice(
        'Ошибка при отправке отклика: ' + (error as Error).message,
        Severity.error,
      )
    }
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <div className={style.modal}>
        <h1>
          {<DescriptionOutlinedIcon sx={{ fontSize: 32 }} />}
          <span className='h1'>Откликнуться на монтаж</span>
        </h1>
        <span className={classNames(style.subtitle)}>
          {'№ ' + description.name}
        </span>
        <ModalCloseButton onClose={handleClose} />
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(handleSubmitOrderForm)}>
            <Controller
              name='price'
              control={control}
              render={({ field }) => (
                <TextField
                  label='Предлагаемая стоимость *'
                  placeholder='Укажите итоговую сумму'
                  type='text'
                  inputProps={{
                    enterKeyHint: 'enter',
                    inputMode: 'numeric',
                    autoComplete: 'off',
                    maxLength: 15,
                  }}
                  data-testid='lkm-offer-price'
                  {...field}
                  error={!!errors.price}
                  helperText={errors.price?.message}
                  onChange={e => {
                    field.onChange(e.target.value.replace(/[^0-9]/g, ''))
                  }}
                />
              )}
            />

            <LkmAppealOfferDateField
              setValidDate={setValidDate}
              isValidDate={isValidDate}
            />

            <Controller
              name='comment'
              control={control}
              render={({ field }) => (
                <TextField
                  label='Комментарий'
                  multiline
                  inputProps={{
                    maxLength: 250,
                    minRows: 2,
                    autoComplete: 'off',
                  }}
                  data-testid='lkm-offer-comment'
                  error={!!errors.comment}
                  helperText={errors.comment?.message}
                  {...field}
                  onChange={e => {
                    field.onChange(e.target.value)
                  }}
                />
              )}
            />
            <LkmAppealOfferButtons
              isSubmitButtonDisabled={isSubmitButtonDisabled}
              isLoading={isLoading}
              handleClose={handleClose}
            />
          </form>
        </FormProvider>
      </div>
    </Modal>
  )
}
